import type { CustomPricingContext } from "@classes-service-types/index.ts";
import { computed, ref } from "vue";
import { MAX_BULK_UPDATE_ROWS } from "../constants";

/** PrimeVue's `v-model:selection` target, so it holds rows rather than the ids the request needs. */
const selectedRows = ref<CustomPricingContext[]>([]);

export const useRowSelection = () => {
  const selectedIds = computed<string[]>(() => selectedRows.value.map((row) => row.id));
  const selectedCount = computed<number>(() => selectedRows.value.length);
  const hasSelection = computed<boolean>(() => selectedRows.value.length > 0);

  /** Selection spans pages, so it can outgrow what one request may carry. */
  const isOverCap = computed<boolean>(() => selectedRows.value.length > MAX_BULK_UPDATE_ROWS);
  const canBulkUpdate = computed<boolean>(() => hasSelection.value && !isOverCap.value);

  const clearSelection = (): void => {
    selectedRows.value = [];
  };

  /**
   * Re-point selected rows at freshly loaded data, so counts read off current values rather than a
   * snapshot taken when the row was ticked. Rows that are off screen are kept, not dropped: the
   * selection deliberately survives page, filter, search and sort changes.
   */
  const setRenderedRows = (rows: CustomPricingContext[]): void => {
    const renderedById = new Map(rows.map((row) => [row.id, row]));
    const refreshed = selectedRows.value.map((row) => renderedById.get(row.id) ?? row);
    if (refreshed.some((row, index) => row !== selectedRows.value[index])) {
      selectedRows.value = refreshed;
    }
  };

  return {
    selectedRows,
    selectedIds,
    selectedCount,
    hasSelection,
    isOverCap,
    canBulkUpdate,
    clearSelection,
    setRenderedRows,
  };
};
